import {useCallback, useEffect, useRef, useState} from "react";
import FileCopyOutlinedIcon from "@mui/icons-material/FileCopyOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import SaveIcon from "@mui/icons-material/Save";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import UndoIcon from "@mui/icons-material/Undo";
import Tooltip from "@mui/material/Tooltip";

import "./index.scss";

// ── Tool types ────────────────────────────────────────────────────────────────

const TOOLS = [
	{id: "select", label: "Sposta", icon: "↖"},
	{id: "draw", label: "Disegno", icon: "✏"},
	{id: "arrow", label: "Freccia", icon: "→"},
	{id: "curvedArrow", label: "Curva", icon: "↝"},
	{id: "line", label: "Linea", icon: "—"},
	{id: "dashedLine", label: "Tratteggiata", icon: "┅"},
	{id: "cone", label: "Cono", icon: "▲"},
	{id: "pole", label: "Paletto", icon: "│"},
	{id: "ladder", label: "Scaletta", icon: "≡"},
	{id: "ball", label: "Pallone", icon: "⚽"},
	{id: "miniGoal", label: "Porticina", icon: "⊓"},
	{id: "player", label: "Giocatore", icon: "●"},
	{id: "opponent", label: "Avversario", icon: "○"},
	{id: "text", label: "Testo", icon: "T"},
	{id: "eraser", label: "Cancella", icon: "✕"},
];

const COLORS = [
	{id: "red", value: "#dc2626", label: "Rosso"},
	{id: "blue", value: "#2563eb", label: "Blu"},
	{id: "yellow", value: "#f59e0b", label: "Giallo"},
	{id: "white", value: "#ffffff", label: "Bianco"},
	{id: "black", value: "#1e293b", label: "Nero"},
	{id: "orange", value: "#f97316", label: "Arancio"},
];

// ── Pitch drawing (ORIZZONTALE) ───────────────────────────────────────────────

function drawPitch(ctx, w, h) {
	ctx.fillStyle = "#2d8c3c";
	ctx.fillRect(0, 0, w, h);

	const stripeH = h / 12;
	ctx.fillStyle = "#329642";
	for (let i = 0; i < 12; i += 2) {
		ctx.fillRect(0, i * stripeH, w, stripeH);
	}

	ctx.strokeStyle = "rgba(255,255,255,0.7)";
	ctx.lineWidth = 2;

	const pad = 30;
	const fw = w - pad * 2;
	const fh = h - pad * 2;

	ctx.strokeRect(pad, pad, fw, fh);

	// Linea meta campo (verticale al centro)
	ctx.beginPath();
	ctx.moveTo(pad + fw / 2, pad);
	ctx.lineTo(pad + fw / 2, pad + fh);
	ctx.stroke();

	// Cerchio centro
	ctx.beginPath();
	ctx.arc(pad + fw / 2, pad + fh / 2, fw * 0.08, 0, Math.PI * 2);
	ctx.stroke();

	ctx.fillStyle = "rgba(255,255,255,0.7)";
	ctx.beginPath();
	ctx.arc(pad + fw / 2, pad + fh / 2, 3, 0, Math.PI * 2);
	ctx.fill();

	// Area grande (sinistra)
	const areaH = fh * 0.44;
	const areaW = fw * 0.16;
	ctx.strokeRect(pad, pad + (fh - areaH) / 2, areaW, areaH);

	// Area piccola (sinistra)
	const smallH = fh * 0.2;
	const smallW = fw * 0.06;
	ctx.strokeRect(pad, pad + (fh - smallH) / 2, smallW, smallH);

	// Area grande (destra)
	ctx.strokeRect(pad + fw - areaW, pad + (fh - areaH) / 2, areaW, areaH);

	// Area piccola (destra)
	ctx.strokeRect(pad + fw - smallW, pad + (fh - smallH) / 2, smallW, smallH);

	// Arco area (sinistra)
	ctx.beginPath();
	ctx.arc(pad + areaW, pad + fh / 2, fw * 0.05, -Math.PI / 2, Math.PI / 2);
	ctx.stroke();

	// Arco area (destra)
	ctx.beginPath();
	ctx.arc(pad + fw - areaW, pad + fh / 2, fw * 0.05, Math.PI / 2, -Math.PI / 2);
	ctx.stroke();

	// Corner arcs
	const cornerR = 8;
	[
		[pad, pad, 0, Math.PI / 2],
		[pad + fw, pad, Math.PI / 2, Math.PI],
		[pad, pad + fh, -Math.PI / 2, 0],
		[pad + fw, pad + fh, Math.PI, Math.PI * 1.5],
	].forEach(([x, y, start, end]) => {
		ctx.beginPath();
		ctx.arc(x, y, cornerR, start, end);
		ctx.stroke();
	});
}

// ── Drawing helpers ───────────────────────────────────────────────────────────

function drawArrowHead(ctx, fromX, fromY, toX, toY, size = 10) {
	const angle = Math.atan2(toY - fromY, toX - fromX);
	ctx.beginPath();
	ctx.moveTo(toX, toY);
	ctx.lineTo(
		toX - size * Math.cos(angle - Math.PI / 6),
		toY - size * Math.sin(angle - Math.PI / 6),
	);
	ctx.lineTo(
		toX - size * Math.cos(angle + Math.PI / 6),
		toY - size * Math.sin(angle + Math.PI / 6),
	);
	ctx.closePath();
	ctx.fill();
}

function drawCone(ctx, x, y, color, size = 12) {
	ctx.fillStyle = color;
	ctx.beginPath();
	ctx.moveTo(x, y - size);
	ctx.lineTo(x - size * 0.7, y + size * 0.4);
	ctx.lineTo(x + size * 0.7, y + size * 0.4);
	ctx.closePath();
	ctx.fill();
	ctx.strokeStyle = "rgba(0,0,0,0.3)";
	ctx.lineWidth = 1;
	ctx.stroke();
}

function drawPole(ctx, x, y, color) {
	ctx.strokeStyle = color;
	ctx.lineWidth = 3;
	ctx.beginPath();
	ctx.moveTo(x, y - 16);
	ctx.lineTo(x, y + 16);
	ctx.stroke();
	ctx.fillStyle = color;
	ctx.beginPath();
	ctx.arc(x, y - 16, 4, 0, Math.PI * 2);
	ctx.fill();
}

function drawLadder(ctx, x, y, color) {
	ctx.strokeStyle = color;
	ctx.lineWidth = 2;
	ctx.strokeRect(x - 10, y - 18, 20, 36);
	for (let i = -12; i <= 12; i += 8) {
		ctx.beginPath();
		ctx.moveTo(x - 10, y + i);
		ctx.lineTo(x + 10, y + i);
		ctx.stroke();
	}
}

function drawBall(ctx, x, y) {
	ctx.fillStyle = "#fff";
	ctx.beginPath();
	ctx.arc(x, y, 8, 0, Math.PI * 2);
	ctx.fill();
	ctx.strokeStyle = "#333";
	ctx.lineWidth = 1.5;
	ctx.stroke();
	// Pentagon pattern
	ctx.fillStyle = "#333";
	ctx.beginPath();
	ctx.arc(x, y, 4, 0, Math.PI * 2);
	ctx.fill();
}

function drawMiniGoal(ctx, x, y, color) {
	ctx.strokeStyle = color;
	ctx.lineWidth = 3;
	ctx.strokeRect(x - 4, y - 14, 8, 28);
	// Net lines
	ctx.lineWidth = 0.5;
	ctx.strokeStyle = "rgba(0,0,0,0.2)";
	for (let i = -10; i <= 10; i += 5) {
		ctx.beginPath();
		ctx.moveTo(x - 4, y + i);
		ctx.lineTo(x + 4, y + i);
		ctx.stroke();
	}
}

function drawPlayerMarker(ctx, x, y, color, label, isOpponent = false) {
	const r = 14;
	ctx.beginPath();
	ctx.arc(x, y, r, 0, Math.PI * 2);
	if (isOpponent) {
		ctx.fillStyle = "#fff";
		ctx.fill();
		ctx.strokeStyle = color;
		ctx.lineWidth = 2.5;
		ctx.stroke();
	} else {
		ctx.fillStyle = color;
		ctx.fill();
	}
	if (label) {
		ctx.fillStyle = isOpponent ? color : "#fff";
		ctx.font = "bold 10px Helvetica, Arial, sans-serif";
		ctx.textAlign = "center";
		ctx.textBaseline = "middle";
		ctx.fillText(label, x, y);
	}
}

function drawCurvedArrow(ctx, from, to, color, lw) {
	const mx = (from.x + to.x) / 2;
	const my = (from.y + to.y) / 2;
	const dx = to.x - from.x;
	const dy = to.y - from.y;
	const cx = mx - dy * 0.3;
	const cy = my + dx * 0.3;

	ctx.strokeStyle = color;
	ctx.lineWidth = lw;
	ctx.setLineDash([]);
	ctx.beginPath();
	ctx.moveTo(from.x, from.y);
	ctx.quadraticCurveTo(cx, cy, to.x, to.y);
	ctx.stroke();

	ctx.fillStyle = color;
	const angle = Math.atan2(to.y - cy, to.x - cx);
	ctx.beginPath();
	ctx.moveTo(to.x, to.y);
	ctx.lineTo(
		to.x - 10 * Math.cos(angle - Math.PI / 6),
		to.y - 10 * Math.sin(angle - Math.PI / 6),
	);
	ctx.lineTo(
		to.x - 10 * Math.cos(angle + Math.PI / 6),
		to.y - 10 * Math.sin(angle + Math.PI / 6),
	);
	ctx.closePath();
	ctx.fill();
}

// ── Placeable types (have x, y) ───────────────────────────────────────────────

const PLACEABLE_TYPES = new Set([
	"cone",
	"pole",
	"ladder",
	"ball",
	"miniGoal",
	"player",
	"opponent",
	"text",
]);

// ── Component ─────────────────────────────────────────────────────────────────

export default function TacticalBoard({
	storageKey = "tactical_default",
	onSaveAttachment,
	initialDrawing,
	editing = false,
}) {
	const saveInProgress = useRef(false);
	const [saving, setSaving] = useState(false);
	const [saveError, setSaveError] = useState("");
	const canvasRef = useRef(null);
	const [tool, setTool] = useState("draw");
	const [color, setColor] = useState(COLORS[0].value);
	const [lineWidth, setLineWidth] = useState(3);
	const [elements, setElements] = useState([]);
	const [drawing, setDrawing] = useState(false);
	const [startPos, setStartPos] = useState(null);
	const [currentPath, setCurrentPath] = useState([]);
	const [playerCounter, setPlayerCounter] = useState({team: 1, opponent: 1});
	const [undoStack, setUndoStack] = useState([]);
	const [dragging, setDragging] = useState(null); // {index, offsetX, offsetY}

	const canvasW = 960;
	const canvasH = 620;

	// ── Load saved state ──────────────────────────────────────────────────────

	useEffect(() => {
		let data = initialDrawing;
		try {
			const saved = localStorage.getItem(`tb_${storageKey}`);
			if (saved) data = JSON.parse(saved);
		} catch {
			/* Bozza locale non disponibile. */
		}
		setElements(data?.elements || []);
		setPlayerCounter(data?.playerCounter || {team: 1, opponent: 1});
		setUndoStack([]);
	}, [storageKey, initialDrawing]);

	// ── Render canvas ─────────────────────────────────────────────────────────

	const renderCanvas = useCallback(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const ctx = canvas.getContext("2d");

		drawPitch(ctx, canvasW, canvasH);
		elements.forEach((el) => renderElement(ctx, el));

		if (drawing && startPos && tool === "draw" && currentPath.length > 1) {
			ctx.strokeStyle = color;
			ctx.lineWidth = lineWidth;
			ctx.lineCap = "round";
			ctx.lineJoin = "round";
			ctx.beginPath();
			ctx.moveTo(currentPath[0].x, currentPath[0].y);
			currentPath.forEach((p) => ctx.lineTo(p.x, p.y));
			ctx.stroke();
		}
	}, [elements, drawing, currentPath, tool, color, lineWidth]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		renderCanvas();
	}, [renderCanvas]);

	const renderElement = (ctx, el) => {
		switch (el.type) {
			case "path":
				ctx.strokeStyle = el.color;
				ctx.lineWidth = el.lineWidth || 3;
				ctx.lineCap = "round";
				ctx.lineJoin = "round";
				ctx.setLineDash([]);
				ctx.beginPath();
				if (el.points?.length > 0) {
					ctx.moveTo(el.points[0].x, el.points[0].y);
					el.points.forEach((p) => ctx.lineTo(p.x, p.y));
				}
				ctx.stroke();
				break;
			case "arrow":
				ctx.strokeStyle = el.color;
				ctx.fillStyle = el.color;
				ctx.lineWidth = el.lineWidth || 3;
				ctx.setLineDash([]);
				ctx.beginPath();
				ctx.moveTo(el.from.x, el.from.y);
				ctx.lineTo(el.to.x, el.to.y);
				ctx.stroke();
				drawArrowHead(ctx, el.from.x, el.from.y, el.to.x, el.to.y, 12);
				break;
			case "curvedArrow":
				drawCurvedArrow(ctx, el.from, el.to, el.color, el.lineWidth || 3);
				break;
			case "line":
				ctx.strokeStyle = el.color;
				ctx.lineWidth = el.lineWidth || 3;
				ctx.setLineDash([]);
				ctx.beginPath();
				ctx.moveTo(el.from.x, el.from.y);
				ctx.lineTo(el.to.x, el.to.y);
				ctx.stroke();
				break;
			case "dashedLine":
				ctx.strokeStyle = el.color;
				ctx.lineWidth = el.lineWidth || 3;
				ctx.setLineDash([8, 6]);
				ctx.beginPath();
				ctx.moveTo(el.from.x, el.from.y);
				ctx.lineTo(el.to.x, el.to.y);
				ctx.stroke();
				ctx.setLineDash([]);
				break;
			case "cone":
				drawCone(ctx, el.x, el.y, el.color);
				break;
			case "pole":
				drawPole(ctx, el.x, el.y, el.color);
				break;
			case "ladder":
				drawLadder(ctx, el.x, el.y, el.color);
				break;
			case "ball":
				drawBall(ctx, el.x, el.y);
				break;
			case "miniGoal":
				drawMiniGoal(ctx, el.x, el.y, el.color);
				break;
			case "player":
				drawPlayerMarker(ctx, el.x, el.y, el.color, el.label, false);
				break;
			case "opponent":
				drawPlayerMarker(ctx, el.x, el.y, el.color, el.label, true);
				break;
			case "text":
				ctx.fillStyle = el.color;
				ctx.font = "bold 14px Helvetica, Arial, sans-serif";
				ctx.textAlign = "left";
				ctx.textBaseline = "top";
				ctx.fillText(el.text, el.x, el.y);
				break;
			default:
				break;
		}
	};

	// ── Get position ──────────────────────────────────────────────────────────

	const getPos = (e) => {
		const canvas = canvasRef.current;
		const rect = canvas.getBoundingClientRect();
		const scaleX = canvasW / rect.width;
		const scaleY = canvasH / rect.height;
		const clientX = e.touches ? e.touches[0].clientX : e.clientX;
		const clientY = e.touches ? e.touches[0].clientY : e.clientY;
		return {
			x: (clientX - rect.left) * scaleX,
			y: (clientY - rect.top) * scaleY,
		};
	};

	const findElementAt = (x, y) => {
		for (let i = elements.length - 1; i >= 0; i--) {
			const el = elements[i];
			if (PLACEABLE_TYPES.has(el.type)) {
				const dist = Math.sqrt((el.x - x) ** 2 + (el.y - y) ** 2);
				if (dist < 20) return i;
			}
			if (el.type === "path" && el.points) {
				for (const p of el.points) {
					if (Math.sqrt((p.x - x) ** 2 + (p.y - y) ** 2) < 10) return i;
				}
			}
			if (el.from && el.to) {
				const dx = el.to.x - el.from.x;
				const dy = el.to.y - el.from.y;
				const len = Math.sqrt(dx * dx + dy * dy);
				if (len === 0) continue;
				const t = Math.max(
					0,
					Math.min(
						1,
						((x - el.from.x) * dx + (y - el.from.y) * dy) / (len * len),
					),
				);
				const px = el.from.x + t * dx;
				const py = el.from.y + t * dy;
				if (Math.sqrt((x - px) ** 2 + (y - py) ** 2) < 10) return i;
			}
		}
		return -1;
	};

	// ── Mouse/touch handlers ──────────────────────────────────────────────────

	const handleStart = (e) => {
		e.preventDefault();
		if (saving) return;
		const pos = getPos(e);

		// Sposta
		if (tool === "select") {
			const idx = findElementAt(pos.x, pos.y);
			if (idx !== -1) {
				const el = elements[idx];
				setUndoStack([...undoStack, elements.map((e) => ({...e}))]);
				setDragging({
					index: idx,
					origin: el,
					start: pos,
					offsetX: pos.x - (el.x ?? 0),
					offsetY: pos.y - (el.y ?? 0),
				});
			}
			return;
		}

		if (tool === "eraser") {
			const idx = findElementAt(pos.x, pos.y);
			if (idx !== -1) {
				setUndoStack([...undoStack, [...elements]]);
				const next = elements.filter((_, i) => i !== idx);
				setElements(next);
				saveState(next);
			}
			return;
		}

		// Placeable objects
		const placeableMap = {
			cone: () => ({type: "cone", x: pos.x, y: pos.y, color}),
			pole: () => ({type: "pole", x: pos.x, y: pos.y, color}),
			ladder: () => ({type: "ladder", x: pos.x, y: pos.y, color}),
			ball: () => ({type: "ball", x: pos.x, y: pos.y}),
			miniGoal: () => ({type: "miniGoal", x: pos.x, y: pos.y, color}),
			player: () => {
				const label = String(playerCounter.team);
				setPlayerCounter((pc) => ({...pc, team: pc.team + 1}));
				return {type: "player", x: pos.x, y: pos.y, color, label};
			},
			opponent: () => {
				const label = String(playerCounter.opponent);
				setPlayerCounter((pc) => ({...pc, opponent: pc.opponent + 1}));
				return {type: "opponent", x: pos.x, y: pos.y, color, label};
			},
			text: () => {
				const text = prompt("Testo:");
				return text ? {type: "text", x: pos.x, y: pos.y, color, text} : null;
			},
		};

		if (placeableMap[tool]) {
			const el = placeableMap[tool]();
			if (el) addElement(el);
			return;
		}

		setDrawing(true);
		setStartPos(pos);
		if (tool === "draw") setCurrentPath([pos]);
	};

	const handleMove = (e) => {
		e.preventDefault();
		const pos = getPos(e);

		// Drag in corso
		if (dragging) {
			const newElements = [...elements];
			const el = {...dragging.origin};
			const dx = pos.x - dragging.start.x,
				dy = pos.y - dragging.start.y;
			if (el.from && el.to) {
				el.from = {
					x: dragging.origin.from.x + dx,
					y: dragging.origin.from.y + dy,
				};
				el.to = {x: dragging.origin.to.x + dx, y: dragging.origin.to.y + dy};
			} else if (el.points) {
				el.points = dragging.origin.points.map((p) => ({
					x: p.x + dx,
					y: p.y + dy,
				}));
			} else {
				el.x = pos.x - dragging.offsetX;
				el.y = pos.y - dragging.offsetY;
			}
			newElements[dragging.index] = el;
			setElements(newElements);
			return;
		}

		if (!drawing) return;

		if (tool === "draw") {
			setCurrentPath((prev) => [...prev, pos]);
		} else {
			const canvas = canvasRef.current;
			const ctx = canvas.getContext("2d");
			drawPitch(ctx, canvasW, canvasH);
			elements.forEach((el) => renderElement(ctx, el));

			ctx.strokeStyle = color;
			ctx.fillStyle = color;
			ctx.lineWidth = lineWidth;

			if (tool === "curvedArrow") {
				drawCurvedArrow(ctx, startPos, pos, color, lineWidth);
			} else {
				if (tool === "dashedLine") ctx.setLineDash([8, 6]);
				else ctx.setLineDash([]);
				ctx.beginPath();
				ctx.moveTo(startPos.x, startPos.y);
				ctx.lineTo(pos.x, pos.y);
				ctx.stroke();
				ctx.setLineDash([]);
				if (tool === "arrow")
					drawArrowHead(ctx, startPos.x, startPos.y, pos.x, pos.y, 12);
			}
		}
	};

	const handleEnd = (e) => {
		e.preventDefault();

		if (dragging) {
			saveState(elements);
			setDragging(null);
			return;
		}

		if (!drawing) return;

		if (tool === "draw" && currentPath.length > 1) {
			addElement({type: "path", points: currentPath, color, lineWidth});
		} else if ((tool === "arrow" || tool === "curvedArrow") && startPos) {
			const pos = e.changedTouches ? getPos(e.changedTouches[0]) : getPos(e);
			addElement({
				type: tool,
				from: startPos,
				to: pos || startPos,
				color,
				lineWidth,
			});
		} else if ((tool === "line" || tool === "dashedLine") && startPos) {
			const pos = e.changedTouches ? getPos(e.changedTouches[0]) : getPos(e);
			addElement({
				type: tool,
				from: startPos,
				to: pos || startPos,
				color,
				lineWidth,
			});
		}

		setDrawing(false);
		setStartPos(null);
		setCurrentPath([]);
	};

	// ── Element management ────────────────────────────────────────────────────

	const addElement = (el) => {
		const newElements = [...elements, el];
		setUndoStack([...undoStack, [...elements]]);
		setElements(newElements);
		saveState(newElements);
	};

	const saveState = (els) => {
		try {
			localStorage.setItem(
				`tb_${storageKey}`,
				JSON.stringify({
					elements: els,
					playerCounter: {
						team: Math.max(
							playerCounter.team,
							...els
								.filter((e) => e.type === "player")
								.map((e) => Number(e.label) + 1),
						),
						opponent: Math.max(
							playerCounter.opponent,
							...els
								.filter((e) => e.type === "opponent")
								.map((e) => Number(e.label) + 1),
						),
					},
				}),
			);
		} catch {
			/* Bozza disponibile nella sessione corrente. */
		}
	};

	const handleUndo = () => {
		if (!undoStack.length) return;
		const prev = undoStack[undoStack.length - 1];
		setElements(prev);
		setUndoStack(undoStack.slice(0, -1));
		saveState(prev);
	};

	const handleClear = () => {
		setUndoStack([...undoStack, [...elements]]);
		setElements([]);
		setPlayerCounter({team: 1, opponent: 1});
		localStorage.removeItem(`tb_${storageKey}`);
	};

	const handleExport = () => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const link = document.createElement("a");
		link.download = "lavagna_tattica.png";
		link.href = canvas.toDataURL("image/png");
		link.click();
	};

	const handleSaveAttachment = async (asNew = false) => {
		if (!canvasRef.current || saveInProgress.current) return;
		saveInProgress.current = true;
		setSaving(true);
		setSaveError("");
		try {
			const blob = await new Promise((resolve, reject) => {
				canvasRef.current.toBlob(
					(image) => (image ? resolve(image) : reject(new Error())),
					"image/png",
				);
			});
			await onSaveAttachment(
				blob,
				{
					schemaVersion: 1,
					width: canvasW,
					height: canvasH,
					elements,
					playerCounter,
				},
				asNew,
			);
		} catch (error) {
			setSaveError(error.message || "Impossibile salvare la lavagna. Riprova.");
		} finally {
			saveInProgress.current = false;
			setSaving(false);
		}
	};

	// ── Render ────────────────────────────────────────────────────────────────

	return (
		<div className="tb">
			<div className="tb__toolbar">
				<div className="tb__tools">
					{TOOLS.map((t) => (
						<button
							key={t.id}
							className={`tb__tool-btn${tool === t.id ? " tb__tool-btn--active" : ""}`}
							onClick={() => setTool(t.id)}
							title={t.label}
						>
							<span className="tb__tool-icon">{t.icon}</span>
							<span className="tb__tool-label">{t.label}</span>
						</button>
					))}
				</div>
				<div className="tb__colors">
					{COLORS.map((c) => (
						<button
							key={c.id}
							className={`tb__color-btn${color === c.value ? " tb__color-btn--active" : ""}`}
							style={{backgroundColor: c.value}}
							onClick={() => setColor(c.value)}
							title={c.label}
						/>
					))}
				</div>
				<div className="tb__line-width">
					<label className="tb__lw-label">Spessore</label>
					<input
						type="range"
						min="1"
						max="8"
						value={lineWidth}
						onChange={(e) => setLineWidth(parseInt(e.target.value))}
						className="tb__lw-slider"
					/>
				</div>
				<div className="tb__actions">
					<Tooltip title="Annulla ultima modifica">
						<span>
							<button
								type="button"
								className="tb__action-btn"
								aria-label="Annulla ultima modifica"
								onClick={handleUndo}
								disabled={saving || !undoStack.length}
							>
								<UndoIcon fontSize="small" />
							</button>
						</span>
					</Tooltip>
					<Tooltip title="Pulisci lavagna">
						<button
							type="button"
							className="tb__action-btn"
							disabled={saving}
							aria-label="Pulisci lavagna"
							onClick={handleClear}
						>
							<DeleteSweepIcon fontSize="small" />
						</button>
					</Tooltip>
					<Tooltip title="Esporta PNG">
						<button
							type="button"
							className="tb__action-btn"
							aria-label="Esporta PNG"
							onClick={handleExport}
						>
							<DownloadIcon fontSize="small" />
						</button>
					</Tooltip>
					{onSaveAttachment && editing && (
						<Tooltip title="Salva come nuova lavagna">
							<span>
								<button
									type="button"
									className="tb__action-btn"
									aria-label="Salva come nuova lavagna"
									disabled={saving || drawing || !!dragging}
									onClick={() => handleSaveAttachment(true)}
								>
									<FileCopyOutlinedIcon fontSize="small" />
								</button>
							</span>
						</Tooltip>
					)}
					{onSaveAttachment && (
						<Tooltip
							title={
								saving
									? "Salvataggio..."
									: editing
										? "Aggiorna lavagna"
										: "Salva negli allegati"
							}
						>
							<span>
								<button
									type="button"
									className="tb__action-btn tb__action-btn--primary"
									aria-label={
										editing ? "Aggiorna lavagna" : "Salva negli allegati"
									}
									onClick={() => handleSaveAttachment(false)}
									disabled={saving || drawing || !!dragging}
									aria-busy={saving}
								>
									<SaveIcon fontSize="small" />
								</button>
							</span>
						</Tooltip>
					)}
				</div>
			</div>

			{saveError && <p role="alert">{saveError}</p>}
			<div className="tb__canvas-wrap">
				<canvas
					ref={canvasRef}
					width={canvasW}
					height={canvasH}
					className="tb__canvas"
					onMouseDown={handleStart}
					onMouseMove={handleMove}
					onMouseUp={handleEnd}
					onMouseLeave={handleEnd}
					onTouchStart={handleStart}
					onTouchMove={handleMove}
					onTouchEnd={handleEnd}
				/>
			</div>
		</div>
	);
}
